import { type FC, useState } from 'react';
import {
  Button,
  DatePicker,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  toast
} from '@cencosud-cencommerce/paris-uikit';
import type { Value } from '@cencosud-cencommerce/paris-uikit/dist/molecules/DatePicker/DatePicker';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { selectBuyNowIsDatePickerOpen } from '@/Application/Selectors/buyNow/useBuyNowCart';
import { setIsDatePickerOpen } from '@/modules/buy-now/infrastructure/slices';
import { priceDtoToPriceString } from '@/modules/shared/mappers/priceDtoToPriceString';
import { isSameDay } from '@/modules/shared/utils/isSameDay';
import type { DeliveryGroup } from '@/modules/shippingMethod/domain/ShippingMethod';
import {
  DELIVERY_TIME,
  LABEL_CONFIRM,
  LABEL_DELIVERY_COST,
  LABEL_DELIVERY_SCHEDULE,
  LABEL_WHEN_DO_YOU_WANT_TO_RECEIVE
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/ShippingOptions/constants';

export interface DateSelectionModalProps {
  scheduleSelectedDate: Value | null;
  setScheduleSelectedDate: (date: Value | null) => void;
  setSelectedDate: (date: Value | null) => void;
  scheduledOptions: DeliveryGroup['scheduledOptions'];
  onConfirm: (date: Value) => void;
}

export type DateRange = {
  start: Date;
  end: Date;
};

export const DateSelectionModal: FC<DateSelectionModalProps> = ({
  scheduledOptions,
  scheduleSelectedDate,
  setScheduleSelectedDate,
  setSelectedDate: setDateToShippingOption,
  onConfirm
}) => {
  const dispatch = useAppDispatch();
  const isDatePickerOpen = useAppSelector(selectBuyNowIsDatePickerOpen);

  const [selectedDate, setSelectedDate] = useState(scheduleSelectedDate);

  if (!isDatePickerOpen) return null;

  const price = priceDtoToPriceString({
    currencyCode: 'CLP',
    centAmount: parseInt(
      String(scheduledOptions?.[0]?.shipmentCostTotal || '0')
    )
  });
  const options = scheduledOptions?.length ? scheduledOptions[0].options : [];
  const validDates: Date[] = [];
  const validDateRanges: { start: Date; end: Date }[] = [];

  options?.forEach((opt) => {
    const { deliveryDateFrom, deliveryDateTo } = opt;
    const d1 = new Date(deliveryDateFrom);
    const d2 = new Date(deliveryDateTo);
    if (isSameDay(d1, d2)) {
      validDates.push(d1);
    } else {
      validDateRanges.push({ start: d1, end: d2 });
    }
  });

  const handleClose = () => {
    dispatch(setIsDatePickerOpen(false));
  };

  const handleConfirm = () => {
    try {
      if (!selectedDate) return;

      handleClose();
      setScheduleSelectedDate(selectedDate);
      setDateToShippingOption(selectedDate);
      onConfirm(selectedDate);
      toast({
        title: 'Cambiaste la fecha con éxito'
      });
    } catch (error) {
      toast({
        title: 'No pudimos cambiar la fecha',
        status: 'error'
      });
    }
  };

  return (
    <Modal isOpen={isDatePickerOpen} size="sm" onClose={handleClose}>
      <ModalHeader
        onClose={handleClose}
        title="Despacho"
        icon={{
          name: 'il-book-service',
          fill: 'primary-500'
        }}
      />
      <ModalBody className="co-p-0 co-m-0">
        <div className="co-p-6 co-pt-2 co-m-0 co-gap-2 co-flex co-flex-col">
          <Text
            variant="body2"
            color="neutral-900"
            weight={600}
            className="co-p-4"
          >
            {LABEL_WHEN_DO_YOU_WANT_TO_RECEIVE}
          </Text>

          <div className="co-flex co-justify-center">
            <DatePicker
              onChange={(date) => {
                setSelectedDate(date);
              }}
              validDateRanges={validDateRanges}
              validDates={validDates}
              value={selectedDate}
            />
          </div>

          <div className="co-flex co-flex-col co-gap-2 co-p-3 co-border co-border-neutral-200 co-rounded-lg">
            <div className="co-flex co-justify-between co-gap-2">
              <Text variant="body2" color="neutral-900" weight={600}>
                {LABEL_DELIVERY_SCHEDULE}
              </Text>
              <Text variant="body2" color="neutral-900" weight={400}>
                {DELIVERY_TIME}
              </Text>
            </div>

            <div className="co-flex co-justify-between co-gap-2">
              <Text variant="body2" color="neutral-900" weight={600}>
                {LABEL_DELIVERY_COST}
              </Text>
              <Text weight={700} variant="body2" color="primary-500">
                {price}
              </Text>
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="co-justify-end">
        <Button
          onClick={handleConfirm}
          disabled={!selectedDate}
          className="co-w-full tablet:co-w-auto"
        >
          {LABEL_CONFIRM}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
